// src/components/ReminderModal/ReminderModal.jsx
import React, { useState } from 'react';
import Modal from 'react-modal';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FiBell, FiX, FiAlertTriangle, FiCheckCircle, FiClock, FiChevronRight, FiCalendar } from 'react-icons/fi';
import './ReminderModal.css';

Modal.setAppElement('#root');

const API_BASE_URL = 'https://uk.backend.hms.hiradentallab.com';

/* ============================================================
   Get status for a single reminder
   ============================================================ */
const getReminderStatus = (reminder) => {
  const now = Date.now();
  const dueAt = new Date(reminder.reminderDueDate).getTime();
  const diffMs = dueAt - now;

  if (diffMs < 0) {
    const daysOverdue = Math.floor(Math.abs(diffMs) / (24 * 60 * 60 * 1000));
    return {
      label: 'Reminder',
      type: 'overdue'
    };
  }

  if (diffMs < 24 * 60 * 60 * 1000) {
    return {
      label: 'Due Today',
      type: 'today'
    };
  }

  const days = Math.ceil(diffMs / (24 * 60 * 60 * 1000));
  return {
    label: `In ${days} day${days > 1 ? 's' : ''}`,
    type: 'future'
  };
};

/* ============================================================
   Format date for display
   ============================================================ */
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

/* ============================================================
   Modal
   ============================================================ */
const ReminderModal = ({
  isOpen,
  onClose,
  reminders = [],
  onAcknowledge
}) => {
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);

  const handleAcknowledgeAll = async () => {
    setProcessing(true);
    try {
      const body = {
        userId: localStorage.getItem('userId') || '',
        userName: localStorage.getItem('userName') || '',
        userRole: (localStorage.getItem('userRole') || '').toLowerCase()
      };
      await axios.put(
        `${API_BASE_URL}/api/reminders/acknowledge-all`,
        body,
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (onAcknowledge) onAcknowledge('all');
      onClose();
    } catch (err) {
      console.error('Acknowledge all error:', err);
    } finally {
      setProcessing(false);
    }
  };

  const handleViewAll = () => {
    onClose();
    navigate('/reminders');
  };

  const handleCaseClick = (caseNumber) => {
    onClose();
    navigate(`/case/${caseNumber}`);
  };

  const visibleReminders = reminders.slice(0, 2);
  const overdueCount = reminders.filter(r => r.isOverdue).length;
  const dueTodayCount = reminders.filter(r => r.isDueToday).length;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="rm-modal"
      overlayClassName="rm-overlay"
      contentLabel="Procedure Reminders"
    >
      {/* ============== HEADER ============== */}
      <div className="rm-header">
        <div className="rm-header-icon-wrap">
          <FiBell size={20} />
        </div>
        <div className="rm-header-text">
          <h2>Reminders</h2>
          <p>
            {reminders.length} due
            {overdueCount > 0 && (
              <> · <span className="rm-overdue-text">{overdueCount} Reminder</span></>
            )}
            {dueTodayCount > 0 && overdueCount === 0 && (
              <> · <span style={{ color: '#d97706' }}>{dueTodayCount} today</span></>
            )}
          </p>
        </div>
        <button className="rm-close" onClick={onClose} aria-label="Close">
          <FiX size={20} />
        </button>
      </div>

      {/* ============== BODY ============== */}
      <div className="rm-body">
        {visibleReminders.length === 0 ? (
          <div className="rm-empty">
            <FiCheckCircle size={42} />
            <p>All caught up!</p>
            <span>No pending reminders</span>
          </div>
        ) : (
          <div className="rm-list">
            {visibleReminders.map((reminder) => {
              const status = getReminderStatus(reminder);

              return (
                <div
                  key={reminder._id}
                  className={`rm-card rm-card-${status.type}`}
                  onClick={() => handleCaseClick(reminder.caseNumber)}
                >
                  <div className="rm-card-accent" />

                  <div className={`rm-card-icon rm-card-icon-${status.type}`}>
                    {status.type === 'overdue'
                      ? <FiAlertTriangle size={16} />
                      : status.type === 'today'
                        ? <FiCalendar size={16} />
                        : <FiClock size={16} />}
                  </div>

                  <div className="rm-card-content">
                    <div className="rm-card-title">
                      <span className="rm-case-number">Case {reminder.caseNumber}</span>
                      <span className="rm-dot">·</span>
                      <span className="rm-patient">
                        {reminder.patientName || 'Unknown Patient'}
                      </span>
                    </div>
                    <div className="rm-card-subtitle">
                      Due: {formatDate(reminder.reminderDueDate)}
                    </div>
                  </div>

                  <div className="rm-card-right">
                    <span className={`rm-badge rm-badge-${status.type}`}>
                      {status.label}
                    </span>
                  </div>

                  <FiChevronRight className="rm-chevron" size={14} />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ============== FOOTER ============== */}
      <div className="rm-footer">
        <button
          className="rm-btn rm-btn-primary"
          onClick={handleViewAll}
        >
          View All Reminders →
        </button>
      </div>
    </Modal>
  );
};

export default ReminderModal;